import {
  Control,
  Controller,
  FieldError,
  FieldErrors,
  RegisterOptions,
} from "react-hook-form";
import { ErrorMessage } from "./ErrorMessage";
import {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  createContext,
  useContext,
} from "react";
import { NoxusFormLabel } from "./NoxusFormLabel";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/shadcn/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { Button } from "@/components/shadcn/button";
import { Check, ChevronsUpDown, Clock, GitBranch, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { AssistantsService, AssistantVersion } from "@/generated";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { CURRENT_VERSION, versionIdForApi } from "@/utils/versions";
import { CurrentVersionWarning } from "./CurrentVersionWarning";
import { timeAgo } from "@/utils/generic";
import { Skeleton } from "@/components/shadcn/skeleton";
import { Banner } from "../Banner";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useRouter } from "next/navigation";
import Link from "next/link";

const MAX_VERSIONS_PAGE = 10;

interface AssistantVersionOption {
  value: string;
  label: string;
  version: AssistantVersion;
}

interface IAssistantVersionInputProps {
  label?: string;
  placeholder?: string;
  tooltip?: string;
  value?: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
  name?: string;
  onlyAssistantSupport?: boolean;
  disableDeselection?: boolean;
  allowCurrentVersion?: boolean;
  // Deployment call sites: warn while the selection tracks the agent's current
  // definition. Off elsewhere ("current" just means "the live draft").
  warnOnCurrentVersion?: boolean;
  // Adds an "All versions" choice (id "all") — used by the export dialog,
  // where exporting every saved version is the default.
  allowAllVersions?: boolean;
  disablePortal?: boolean;
  showErrorbanner?: boolean;
  onValidityChange?: (isInvalid: boolean) => void;
}

export const AssistantVersionInput = (props: IAssistantVersionInputProps) => {
  const assistantId = useContext(AssistantVersionContext);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { spotGroup } = useSpotUser();

  const observer = useRef<IntersectionObserver>(undefined);

  const hasPortal = !props.disablePortal;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isError,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [DATA_KEYS.ASSISTANT_VERSIONS, assistantId, spotGroup.id],
    queryFn: ({ pageParam = 1 }) =>
      AssistantsService.getAssistantVersions({
        path: {
          group_id: spotGroup.id,
          assistant_id: assistantId || "",
        },
        query: {
          page: pageParam,
          size: MAX_VERSIONS_PAGE,
        },
      }),
    enabled: !!assistantId,
    placeholderData: keepPreviousData,
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.data.page) return;
      return firstPage.data.page > 1 ? firstPage.data.page - 1 : undefined;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.data.page || !lastPage.data.pages) return;

      return lastPage.data.page < lastPage.data.pages
        ? allPages.length + 1
        : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading || isFetching) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetching, isLoading]
  );

  const versions = useMemo(() => {
    const vs = data?.pages?.flatMap((page) => page.data.items);
    const synthetic: { id: string; name: string }[] = [];
    if (props.allowAllVersions) {
      synthetic.push({ id: "all", name: "All versions" });
    }
    if (props.allowCurrentVersion) {
      synthetic.push({ id: CURRENT_VERSION, name: "Current version" });
    }
    if (synthetic.length > 0) {
      return [...synthetic, ...(vs || [])];
    }
    return vs;
  }, [data?.pages, props.allowCurrentVersion, props.allowAllVersions]);

  const router = useRouter();

  const filteredVersions = useMemo(() => {
    if (!versions) return [];
    if (!searchValue.trim()) return versions;

    return versions.filter((version) =>
      version.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [versions, searchValue]);

  const selectedVersion = useMemo(() => {
    return versions?.find((v) => v.id === props.value);
  }, [versions, props.value]);

  const versionIdForValidation =
    props.value && props.value !== CURRENT_VERSION && props.value !== "all"
      ? props.value
      : undefined;
  const { data: validationResult } = useQuery({
    queryKey: [DATA_KEYS.ASSISTANT_ID, "valid", assistantId, props.value],
    queryFn: () =>
      AssistantsService.getIsValidAssistant({
        path: { group_id: spotGroup.id, assistant_id: assistantId || "" },
        query: { version_id: versionIdForValidation },
      }),
    enabled: !!assistantId && !!props.value,
    staleTime: 60_000,
  });
  const isSelectedInvalid = validationResult?.data?.is_valid === false;

  const onValidityChange = props.onValidityChange;
  useEffect(() => {
    onValidityChange?.(isSelectedInvalid);
  }, [isSelectedInvalid, onValidityChange]);

  const invalidReasons = useMemo(() => {
    const exceptions = validationResult?.data?.exceptions;
    if (!exceptions) return [];
    return Object.values(exceptions)
      .flat()
      .map((err) => {
        const snippet = typeof err.snippet === "string" ? err.snippet : "";
        const detail = typeof err.detail === "string" ? err.detail : "";
        return snippet || detail;
      })
      .filter(Boolean);
  }, [validationResult]);

  const { data: assistantData } = useRemoteData(
    DATA_KEYS.ASSISTANT_ID,
    {
      path: {
        group_id: spotGroup.id,
        assistant_id: assistantId || "",
      },
    },
    {
      enabled: !!assistantId && props.allowCurrentVersion,
    }
  );

  if (!assistantId) {
    if (props.showErrorbanner) {
      return (
        <Banner
          label="The assistant version is not set. Please contact support."
          style="error"
        />
      );
    }
    return <></>;
  }

  return (
    <div className="w-full">
      {props.label && (
        <NoxusFormLabel
          label={props.label}
          tooltip={props.tooltip}
          name={props.name}
        />
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between font-normal focus-within:ring-ring focus-within:ring-1",
              open && "ring-ring ring-1"
            )}
            disabled={props.isDisabled}
          >
            {selectedVersion?.name || props.placeholder || "Select version..."}
            <ChevronsUpDown className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[400px] p-0 z-5000 pointer-events-auto"
          align="start"
          hasPortal={hasPortal}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search versions..."
              className="h-9"
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              <CommandEmpty>
                <p className="mb-2">
                  {isError
                    ? "Error loading versions"
                    : isLoading
                      ? "Loading..."
                      : searchValue.trim()
                        ? "No versions match your search."
                        : "No versions found."}
                </p>
                {!isError && !isLoading && !searchValue.trim() && (
                  <Link
                    href={`/assistant/${assistantId}?createVersion=true`}
                    target="_blank"
                    className="underline"
                  >
                    <Button variant="outline">Create a new version</Button>
                  </Link>
                )}
              </CommandEmpty>
              <CommandGroup className="max-h-[250px] overflow-y-auto overflow-x-hidden">
                {!isLoading &&
                  filteredVersions?.map((version, index) => (
                    <CommandItem
                      key={version.id}
                      value={version.id}
                      onSelect={(currentValue) => {
                        if (
                          props.disableDeselection &&
                          currentValue === props.value
                        ) {
                          setOpen(false);
                          return;
                        }
                        props.onChange(
                          currentValue === props.value ? "" : currentValue
                        );
                        setOpen(false);
                      }}
                    >
                      {version.id === "all" ? (
                        <Layers size={16} className="mr-2" />
                      ) : version.id === CURRENT_VERSION ? (
                        <GitBranch size={16} className="mr-2" />
                      ) : (
                        <Clock size={16} className="mr-2" />
                      )}
                      <div
                        className="flex flex-col items-start flex-1"
                        ref={
                          index === filteredVersions.length - 1
                            ? lastElementRef
                            : undefined
                        }
                      >
                        <span className="font-medium text-overflow-hidden text-ellipsis whitespace-nowrap">
                          {version.name}
                        </span>
                        {(version as AssistantVersion)?.created_at && (
                          <span className="text-xs text-muted-foreground">
                            Saved{" "}
                            {timeAgo(
                              (version as AssistantVersion).created_at
                            ).toLowerCase()}
                          </span>
                        )}
                        {version.id === CURRENT_VERSION &&
                          assistantData &&
                          !!assistantData.last_updated_at && (
                            <span className="text-xs text-muted-foreground">
                              Saved{" "}
                              {timeAgo(
                                assistantData.last_updated_at
                              ).toLowerCase()}
                            </span>
                          )}
                      </div>
                      <Check
                        className={cn(
                          "ml-auto",
                          props.value === version.id
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}

                {isFetchingNextPage && (
                  <div className="p-2">
                    <Skeleton className="h-8" />
                  </div>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {props.warnOnCurrentVersion && versionIdForApi(props.value) === null && (
        <CurrentVersionWarning entity="agent" />
      )}
      {isSelectedInvalid && (
        <NoxusAlert
          className="mt-2"
          variant="warning"
          description={
            invalidReasons.length ? (
              invalidReasons.length === 1 ? (
                invalidReasons[0]
              ) : (
                <ul className="list-disc pl-4">
                  {invalidReasons.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              )
            ) : (
              "This version is invalid and can't be used. Please select a valid version."
            )
          }
        />
      )}
    </div>
  );
};

interface IAssistantVersionFieldProps {
  name: string;
  tooltip?: string;
  label?: string;
  placeholder?: string;
  rules?: RegisterOptions;
  errors?: FieldErrors<any>;
  control: Control<any>;
  defaultValue?: string;
  onChange?: (value: string) => void;
  isDisabled?: boolean;
  disableDeselection?: boolean;
  allowCurrentVersion?: boolean;
  warnOnCurrentVersion?: boolean;
  disablePortal?: boolean;
}

export const AssistantVersionField = (props: IAssistantVersionFieldProps) => {
  const hasError = !!(props.errors && props.errors[props.name]);

  return (
    <div className="flex flex-col w-full gap-1 items-start">
      <Controller
        control={props.control}
        disabled={props.isDisabled}
        defaultValue={props.defaultValue}
        name={props.name}
        rules={props.rules}
        render={({ field: { onChange, value } }) => (
          <AssistantVersionInput
            value={value}
            label={props.label}
            placeholder={props.placeholder}
            isDisabled={props.isDisabled}
            tooltip={props.tooltip}
            onChange={(value) => {
              onChange(value);
              if (props.onChange) props.onChange(value);
            }}
            name={props.name}
            disableDeselection={props.disableDeselection}
            allowCurrentVersion={props.allowCurrentVersion}
            warnOnCurrentVersion={props.warnOnCurrentVersion}
            disablePortal={props.disablePortal}
          />
        )}
      />
      {hasError && (
        <ErrorMessage error={props.errors![props.name] as FieldError} />
      )}
    </div>
  );
};

export const AssistantVersionContext = createContext<string | undefined>(
  undefined
);
